/** @format */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const VIEWPORT_PAD = 8
const HIDE_DELAY_MS = 120
const ANIM_MS = 200
const PANEL_CLASS = 'mouse-follow-hint__panel'
const PANEL_SHOWN_CLASS = 'mouse-follow-hint__panel--shown'
const STYLE_ID = 'mouse-follow-hint-style'

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  zIndex: 1060,
  maxWidth: 'calc(100vw - 16px)',
  boxSizing: 'border-box',
  padding: '12px 16px',
  borderRadius: 4,
  background: '#fff',
  boxShadow:
    '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
}

const PANEL_CSS = `
.${PANEL_CLASS} {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
  transform-origin: top left;
  transition: opacity ${ANIM_MS}ms ease, transform ${ANIM_MS}ms ease;
  pointer-events: none;
}
.${PANEL_CLASS}.${PANEL_SHOWN_CLASS} {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}
.${PANEL_CLASS} .k-descriptions-content { padding: 0; }
.${PANEL_CLASS} .k-description-item { padding: 4px 0; }
@media (prefers-reduced-motion: reduce) {
  .${PANEL_CLASS} {
    transition: none;
    transform: none;
  }
}
`

function ensurePanelStyles() {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ID
    document.head.appendChild(style)
  }
  style.textContent = PANEL_CSS
}

export interface MouseFollowHintProps {
  /** 触发区域（仅支持单个 React 元素） */
  children: React.ReactElement
  /** 浮层内容 */
  content: React.ReactNode
  /** 相对光标的偏移（px），默认在光标右下方 */
  offset?: { x: number; y: number }
  /** 是否允许鼠标移入浮层（内容含可点击链接时建议开启） */
  hoverable?: boolean
  /** 挂载浮层的容器，默认 `document.body` */
  container?: string | HTMLElement | (() => HTMLElement)
  className?: string
}

function resolveContainer(container?: MouseFollowHintProps['container']): HTMLElement {
  if (!container) {
    return document.body
  }
  if (typeof container === 'string') {
    return (document.querySelector(container) as HTMLElement) || document.body
  }
  if (typeof container === 'function') {
    return container() || document.body
  }
  return container
}

const MouseFollowHint: React.FC<MouseFollowHintProps> = ({
  children,
  content,
  offset = { x: 12, y: 12 },
  hoverable = true,
  container,
  className
}) => {
  const [panelMounted, setPanelMounted] = useState(false)
  const [panelShown, setPanelShown] = useState(false)
  const [anchor, setAnchor] = useState({ x: 0, y: 0 })
  const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0 })
  const hideTimerRef = useRef<number>()
  const unmountTimerRef = useRef<number>()
  const popoverRef = useRef<HTMLDivElement>(null)

  const clearHideTimer = () => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = undefined
    }
  }

  const clearUnmountTimer = () => {
    if (unmountTimerRef.current != null) {
      window.clearTimeout(unmountTimerRef.current)
      unmountTimerRef.current = undefined
    }
  }

  const clearTimers = () => {
    clearHideTimer()
    clearUnmountTimer()
  }

  useEffect(() => {
    ensurePanelStyles()
    return () => clearTimers()
  }, [])

  const startHide = useCallback(() => {
    setPanelShown(false)
    clearUnmountTimer()
    unmountTimerRef.current = window.setTimeout(() => {
      setPanelMounted(false)
      unmountTimerRef.current = undefined
    }, ANIM_MS)
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimer()
    hideTimerRef.current = window.setTimeout(() => startHide(), hoverable ? HIDE_DELAY_MS : 0)
  }, [hoverable, startHide])

  const updateAnchor = useCallback(
    (clientX: number, clientY: number) => {
      setAnchor({ x: clientX, y: clientY })
      setPopoverPos({ x: clientX + offset.x, y: clientY + offset.y })
    },
    [offset.x, offset.y]
  )

  const showAt = useCallback(
    (e: React.MouseEvent) => {
      clearTimers()
      updateAnchor(e.clientX, e.clientY)
      if (!panelMounted) {
        setPanelMounted(true)
        requestAnimationFrame(() => setPanelShown(true))
      } else {
        setPanelShown(true)
      }
    },
    [panelMounted, updateAnchor]
  )

  useLayoutEffect(() => {
    if (!panelMounted || !panelShown) {
      return
    }
    const el = popoverRef.current
    if (!el) {
      return
    }
    const rect = el.getBoundingClientRect()
    let x = anchor.x + offset.x
    let y = anchor.y + offset.y

    if (x + rect.width > window.innerWidth - VIEWPORT_PAD) {
      x = anchor.x - rect.width - offset.x
    }
    if (y + rect.height > window.innerHeight - VIEWPORT_PAD) {
      y = anchor.y - rect.height - offset.y
    }

    setPopoverPos({
      x: Math.max(VIEWPORT_PAD, x),
      y: Math.max(VIEWPORT_PAD, y)
    })
  }, [panelMounted, panelShown, anchor.x, anchor.y, offset.x, offset.y, content])

  const handlePanelTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName !== 'opacity' || panelShown) {
      return
    }
    clearUnmountTimer()
    setPanelMounted(false)
  }

  const handleTriggerMouseEnter = (e: React.MouseEvent) => {
    showAt(e)
    children.props.onMouseEnter?.(e)
  }

  const handleTriggerMouseMove = (e: React.MouseEvent) => {
    updateAnchor(e.clientX, e.clientY)
    children.props.onMouseMove?.(e)
  }

  const handleTriggerMouseLeave = (e: React.MouseEvent) => {
    scheduleHide()
    children.props.onMouseLeave?.(e)
  }

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    startHide()
    children.props.onClick?.(e)
  }

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      startHide()
    }
    children.props.onKeyDown?.(e)
  }

  const trigger = React.cloneElement(children, {
    onMouseEnter: handleTriggerMouseEnter,
    onMouseMove: handleTriggerMouseMove,
    onMouseLeave: handleTriggerMouseLeave,
    onClick: handleTriggerClick,
    onKeyDown: handleTriggerKeyDown
  })

  const popover =
    panelMounted &&
    createPortal(
      <div
        ref={popoverRef}
        className={[PANEL_CLASS, panelShown ? PANEL_SHOWN_CLASS : ''].filter(Boolean).join(' ')}
        style={{ ...PANEL_STYLE, left: popoverPos.x, top: popoverPos.y }}
        onTransitionEnd={handlePanelTransitionEnd}
        onMouseEnter={() => {
          if (hoverable) {
            clearHideTimer()
          }
        }}
        onMouseLeave={() => {
          if (hoverable) {
            scheduleHide()
          }
        }}
      >
        {content}
      </div>,
      resolveContainer(container)
    )

  return (
    <div className={className} style={{ display: 'contents' }}>
      {trigger}
      {popover}
    </div>
  )
}

export default MouseFollowHint
